import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Page from "../molecules/Page";
import SearchModal from "../molecules/SearchModal";
import PaywallPopup from "../molecules/PaywallPopup";
import FieldSheet from "../molecules/FieldSheet";
import SessionSetupPopup from "../molecules/SessionSetupPopup";
import api from "../../api/apiHelper";
import { readApiCache, writeApiCache, fieldDetailsCacheKey } from "../../utils/apiCache";
import { isAccessBlocked, isNoAccessError } from "../../utils/access";
import { useUserDashboard } from "../../contexts/UserDashboardContext";
import { AUTH_TOKEN_KEY } from "../../utils/dashboardUtils";
import { LEVEL_DEFINITIONS, getThresholdsForField } from "../../utils/gamificationConfig";
import { AiOutlineSync } from "react-icons/ai";
import { FaSearch } from "react-icons/fa";
import { FiChevronRight, FiChevronDown, FiBookOpen, FiPlusCircle, FiZap, FiBarChart2 } from "react-icons/fi";

const apiUrl = process.env.REACT_APP_API_SOURCE;
const BASKET_KEY = 'training_basket_details';
const BASKET_TTL = 24 * 60 * 60 * 1000;

const LOADING_STEPS = ['rozwiązujemy zadania', 'przeszukujemy kartę wzorów CKE', 'przygotowujemy Orła AI'];

// --- HELPER: aktualna ranga pola ---
const getCurrentRank = (field) => {
  const thresholds = getThresholdsForField(field.field_id);
  let current = null;
  for (const level of LEVEL_DEFINITIONS) {
    const req = thresholds[level.name];
    if ((field.user_field_elo || 0) >= req.elo && (field.total_solved_per_field || 0) >= req.tasks) {
      current = level;
    }
  }
  return current;
};

// --- JAK TO DZIAŁA ---
const TrainingHowItWorks = () => (
  <div className="space-y-2.5 pb-3">

    <div className="flex items-center gap-2.5">
      <FiZap className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Poziom ELO: </strong> Określa Twój poziom w dziale i trudność tematów.</p>
    </div>
    {/* <div className="flex items-center gap-2.5">
      <FiBarChart2 className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">ELO tematu: </strong> wskażnik trudności tematu.</p>
    </div> */}
        <div className="flex items-center gap-2.5">
      <FiBookOpen className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Ty wybierasz: </strong> Nic nie jest zablokowane, po prostu wybierz dowolny temat. </p>
    </div>
  </div>
);

// --- WIERSZ DZIAŁU (1 kolumna) ---
const FieldRow = ({ field, onOpen }) => {
  const rank = getCurrentRank(field);
  const elo = field.user_field_elo || 0;

  const thresholds = getThresholdsForField(field.field_id);
  const nextRank = LEVEL_DEFINITIONS.find((l) => {
    const req = thresholds[l.name];
    return !(elo >= req.elo && (field.total_solved_per_field || 0) >= req.tasks);
  });
  const nextReq = nextRank ? thresholds[nextRank.name] : null;
  const prevRankIndex = nextRank ? LEVEL_DEFINITIONS.indexOf(nextRank) - 1 : LEVEL_DEFINITIONS.length - 1;
  const prevReq = prevRankIndex >= 0 ? thresholds[LEVEL_DEFINITIONS[prevRankIndex].name] : { elo: 0, tasks: 0 };
  const eloProgress = nextReq
    ? Math.min(100, Math.max(0, Math.round(((elo - prevReq.elo) / (nextReq.elo - prevReq.elo)) * 100)))
    : 100;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left p-3.5 mb-2.5 bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:border-[#233855]/30 hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.97] active:shadow-sm transition-all duration-150 group flex items-center gap-3"
    >
      {/* Ikona rangi */}
      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
        {rank ? (
          <img src={rank.icon.achieved} alt={rank.name} className="w-8 h-8" />
        ) : (
          <img src={LEVEL_DEFINITIONS[0].icon.notAchieved} alt="Brak rangi" className="w-8 h-8 opacity-25" />
        )}
      </div>

      {/* Treść */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="font-semibold text-sm text-[#233855] leading-snug truncate">
            {field.field_name}
          </span>
          <span className="flex-shrink-0 text-xs text-gray-400 font-medium">ELO {elo}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#233855] rounded-full"
              style={{ width: `${eloProgress}%` }}
            />
          </div>
          <span className="flex-shrink-0 text-[11px] text-gray-400">
            {nextRank ? nextRank.name : "Max"}
          </span>
        </div>
      </div>

      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
      </div>
    </button>
  );
};

// --- GŁÓWNY KOMPONENT ---
const Training = ({ setProblemsData, setUserEloDict }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const { dashboardData, isLoading: isDashboardLoading, ensureLoaded } = useUserDashboard();

  const [fields, setFields] = useState(() => dashboardData?.available_fields || []);
  const [accountStatus, setAccountStatus] = useState(() => dashboardData?.account_status || null);
  const [popupUserData, setPopupUserData] = useState(null);

  // Zawartość działu (pełna odpowiedź field-details), leniwie per fieldId.
  // ŚWIADOMIE pusta na starcie: koszyk to kilka wybranych tematów, nie zawartość
  // działu — wstrzyknięty tutaj malował się w arkuszu jako gotowa (i nieprawdziwa)
  // lista. Dopóki nie ma prawdziwych danych, arkusz pokazuje spinner.
  const [subfieldsMap, setSubfieldsMap] = useState({});

  // Nazwy tematów z koszyka — tylko do chipów. Trzymane osobno, żeby nie mieszać
  // ich z zawartością działu (patrz wyżej).
  const [basketNames, setBasketNames] = useState(() => {
    const details = readApiCache(BASKET_KEY, BASKET_TTL)
      ?? readApiCache('user_dashboard_data')?.selected_custom_subfields_details;
    if (!Array.isArray(details)) return {};
    return Object.fromEntries(details.map(s => [s.subfield_id, s.subfield_name]));
  });
  const [loadingFieldId, setLoadingFieldId] = useState(null);
  const fullyLoadedFields = useRef(new Set());

  // Otwarty sheet
  const [selectedFieldId, setSelectedFieldId] = useState(null);

  // Koszyk
  const [basket, setBasket] = useState(() => {
    const details = readApiCache(BASKET_KEY, BASKET_TTL)
      ?? readApiCache('user_dashboard_data')?.selected_custom_subfields_details;
    return Array.isArray(details) ? details.map(s => s.subfield_id) : [];
  });

  // Wyszukiwarka
  const [searchData, setSearchData] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);

  // Paywall
  const [isPaywallPopupOpen, setPaywallPopupOpen] = useState(false);

  // Loading overlay sesji (Orzeł + teksty, jak na Dashboardzie)
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSessionDataReady, setIsSessionDataReady] = useState(false);
  const [sessionMessages, setSessionMessages] = useState(null);
  const sessionDataRef = useRef(null);

  const [isBenefitsOpen, setIsBenefitsOpen] = useState(() => {
    const saved = localStorage.getItem('training_benefits_open');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('training_benefits_open', String(isBenefitsOpen));
  }, [isBenefitsOpen]);


  // Self-heal: jeśli wejdziemy tu bez danych (np. zaraz po logowaniu), dociągnij je
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  // --- INITIAL DATA FROM CONTEXT (przed malowaniem — bez klatki z pustymi danymi) ---
  useLayoutEffect(() => {
    if (!dashboardData) return;
    const data = dashboardData;
    setFields(data.available_fields || []);
    setAccountStatus(data.account_status);
    if (data.account_status) {
      localStorage.setItem("userStatus", JSON.stringify(data.account_status));
      setPopupUserData({
        username: data.username,
        problems_solved_total: data.problems_solved_total,
        current_streak: data.account_status.current_streak,
      });
    }
    // Sync koszyka z serwera tylko jeśli nie ma lokalnego stanu (pierwsze uruchomienie / wygasły cache)
    if (readApiCache(BASKET_KEY, BASKET_TTL) === null) {
      const details = data.selected_custom_subfields_details;
      if (details?.length > 0) {
        writeApiCache(BASKET_KEY, details);
        setBasket(details.map(s => s.subfield_id));
        setBasketNames(prev => ({
          ...prev,
          ...Object.fromEntries(details.map(s => [s.subfield_id, s.subfield_name])),
        }));
      } else {
        writeApiCache(BASKET_KEY, []);
      }
    }
  }, [dashboardData]);

  // --- OTWARCIE SHEETU (lazy load subfieldów) ---
  const handleOpenSheet = async (fieldId) => {
    setSelectedFieldId(fieldId);

    const cacheKey = fieldDetailsCacheKey(fieldId);
    const cached = readApiCache(cacheKey, Infinity); // zawsze pokaż co jest, niezależnie od TTL
    const stale = Array.isArray(cached) ? cached : null; // ignoruj stary/zły kształt z cache

    if (stale) {
      setSubfieldsMap((prev) => ({ ...prev, [fieldId]: stale }));
    }

    if (fullyLoadedFields.current.has(fieldId)) return;

    if (stale) {
      axios.get(`${apiUrl}field-details/${fieldId}/`, {
        headers: { Authorization: `Token ${token}` },
      }).then(({ data }) => {
        const arr = Array.isArray(data) ? data : [];
        setSubfieldsMap((prev) => ({ ...prev, [fieldId]: arr }));
        writeApiCache(cacheKey, arr);
        fullyLoadedFields.current.add(fieldId);
      }).catch(() => {});
      return;
    }

    setLoadingFieldId(fieldId);
    try {
      const { data } = await axios.get(`${apiUrl}field-details/${fieldId}/`, {
        headers: { Authorization: `Token ${token}` },
      });
      const arr = Array.isArray(data) ? data : [];
      setSubfieldsMap((prev) => ({ ...prev, [fieldId]: arr }));
      writeApiCache(cacheKey, arr);
      fullyLoadedFields.current.add(fieldId);
    } catch (err) {
      console.error("Błąd ładowania subfieldów:", err);
    } finally {
      setLoadingFieldId(null);
    }
  };

  // --- KOSZYK ---
  const handleToggleBasket = (subfieldId) => {
    const isAdding = !basket.includes(subfieldId);
    setBasket((prev) => isAdding ? [...prev, subfieldId] : prev.filter(id => id !== subfieldId));

    api.patch('update-custom-subfields/', { action: isAdding ? 'add' : 'remove', subfield_id: subfieldId }, { skipLoading: true }).catch(() => {});

    const currentDetails = readApiCache(BASKET_KEY, BASKET_TTL) ?? [];
    let newDetails;
    if (isAdding) {
      let sf = null;
      for (const [fId, subs] of Object.entries(subfieldsMap)) {
        sf = subs.find(s => s.subfield_id === subfieldId);
        if (sf) { sf = { ...sf, field_id: Number(fId) }; break; }
      }
      newDetails = sf ? [...currentDetails.filter(s => s.subfield_id !== subfieldId), sf] : currentDetails;
    } else {
      newDetails = currentDetails.filter(s => s.subfield_id !== subfieldId);
    }
    writeApiCache(BASKET_KEY, newDetails);
  };

  // --- START SESJI (direct lub koszyk) ---
  const startSession = (subfieldIds, messages) => {
    if (isAccessBlocked(accountStatus)) {
      setPaywallPopupOpen(true);
      return;
    }
    setSessionMessages(messages || null);
    setIsSessionLoading(true);
    setIsSessionDataReady(false);
    sessionDataRef.current = null;
    (async () => {
      try {
        if (accountStatus) {
          localStorage.setItem("userStatusBeforeSession", JSON.stringify({ ...accountStatus }));
        }
        const [statsRes, sessionRes] = await Promise.all([
          api.get("user_stats_view/", { skipLoading: true }),
          api.post("start_solve_problems/", {
            preferences: { level_modifier: 0, mobile_problems: false },
            selected_subfields: subfieldIds,
          }, { skipLoading: true }),
        ]);
        const statsMapping = {};
        statsRes.data?.radar_chart?.fields?.forEach((s) => { statsMapping[s.field_id] = s; });
        localStorage.setItem("fullUserStatsBeforeSession", JSON.stringify(statsMapping));
        localStorage.setItem("sessionStartTime", Math.floor(Date.now() / 1000));
        sessionDataRef.current = sessionRes.data;
        setIsSessionDataReady(true);
      } catch (err) {
        console.error("Błąd startu sesji:", err);
        setIsSessionLoading(false);
        // Serwer odmowil dostepu do poziomu (402) — pokaz paywall.
        if (isNoAccessError(err)) setPaywallPopupOpen(true);
      }
    })();
  };

  // Overlay zostaje do nawigacji — navigate odmontuje Trening razem z popupem w jednym commicie
  const handleSessionComplete = () => {
    const sessionData = sessionDataRef.current;
    sessionDataRef.current = null;
    if (sessionData) {
      setUserEloDict(sessionData.user_elo_dict);
      setProblemsData(sessionData.final_problems);
      localStorage.setItem("userEloDict", JSON.stringify(sessionData.user_elo_dict));
      localStorage.setItem("problemsData", JSON.stringify(sessionData.final_problems));
      navigate("/solve-problem");
    } else {
      setIsSessionLoading(false);
      setIsSessionDataReady(false);
    }
  };

  const handleDirectStart = (subfieldId) => {
    setSelectedFieldId(null);
    const name = subfieldNameMap[subfieldId];
    startSession([subfieldId], name
      ? [`Ładujemy temat: ${name}`, ...LOADING_STEPS]
      : LOADING_STEPS);
  };

  const handleStartBasket = () => {
    setSelectedFieldId(null);
    const n = basket.length;
    const word = n === 1 ? "temat" : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) ? "tematy" : "tematów";
    startSession(basket, [`Przygotowujemy trening · ${n} ${word}`, ...LOADING_STEPS]);
  };

  // --- WYSZUKIWARKA ---
  const handleSearchTrigger = async () => {
    if (searchData.length > 0) { setSearchModalOpen(true); return; }
    setIsSearchLoading(true);
    try {
      const { data } = await api.get("/training-search-data/");
      setSearchData(data);
      setSearchModalOpen(true);
    } catch (err) {
      console.error("Błąd pobierania searcha:", err);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleSearchResultSelect = async (subfieldId, fieldId, subfieldName) => {
    const isAdding = !basket.includes(subfieldId);
    const numFieldId = Number(fieldId);

    setBasket((prev) =>
      isAdding ? [...prev, subfieldId] : prev.filter((id) => id !== subfieldId)
    );

    api.patch('update-custom-subfields/', { action: isAdding ? 'add' : 'remove', subfield_id: subfieldId }, { skipLoading: true }).catch(() => {});

    // Natychmiastowa aktualizacja cache i subfieldsMap — nie czeka na fetch field-details,
    // żeby szybka nawigacja nie gubiła dodanego tematu
    const currentDetails = readApiCache(BASKET_KEY, BASKET_TTL) ?? [];
    if (isAdding) {
      const existingSf = subfieldsMap[numFieldId]?.find(s => s.subfield_id === subfieldId);
      const sf = existingSf
        ? { ...existingSf, field_id: numFieldId }
        : { subfield_id: subfieldId, field_id: numFieldId, subfield_name: subfieldName ?? '' };
      writeApiCache(BASKET_KEY, [...currentDetails.filter(s => s.subfield_id !== subfieldId), sf]);

      // Nazwa od razu w chipie. Do subfieldsMap NIE piszemy — tam idzie wyłącznie
      // pełna odpowiedź field-details, inaczej ten pojedynczy temat udawałby
      // w arkuszu zawartość całego działu.
      setBasketNames(prev => ({ ...prev, [subfieldId]: sf.subfield_name }));
    } else {
      writeApiCache(BASKET_KEY, currentDetails.filter(s => s.subfield_id !== subfieldId));
    }

    // Pobierz pełne dane działu asynchronicznie (zastąpi minimalny wpis pełnymi danymi)
    if (!fullyLoadedFields.current.has(numFieldId)) {
      try {
        const { data } = await api.get(`field-details/${numFieldId}/`);
        setSubfieldsMap((prev) => ({ ...prev, [numFieldId]: data }));
        writeApiCache(fieldDetailsCacheKey(numFieldId), data);
        fullyLoadedFields.current.add(numFieldId);
        if (isAdding) {
          const sf = data.find(s => s.subfield_id === subfieldId);
          if (sf) {
            const latestDetails = readApiCache(BASKET_KEY, BASKET_TTL) ?? [];
            writeApiCache(BASKET_KEY, [...latestDetails.filter(s => s.subfield_id !== subfieldId), { ...sf, field_id: numFieldId }]);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  // --- GRUPOWANIE DZIAŁÓW ---
  const groupedFields = useMemo(() => {
    const groups = {};
    fields.forEach((f) => {
      const key = f.knowledge_level || "Pozostałe";
      groups[key] = groups[key] || [];
      groups[key].push(f);
    });
    return groups;
  }, [fields]);

  const selectedField = selectedFieldId ? fields.find((f) => f.field_id === selectedFieldId) : null;

  // Mapa id → nazwa dla chipów koszyka (na rozszerzeniu id to template_id).
  // Baza to nazwy z koszyka, a wczytane działy je nadpisują — świeższe i pełniejsze.
  const subfieldNameMap = useMemo(() => {
    const map = { ...basketNames };
    Object.values(subfieldsMap).forEach((arr) => {
      if (Array.isArray(arr)) arr.forEach((s) => { map[s.subfield_id] = s.subfield_name; });
    });
    return map;
  }, [subfieldsMap, basketNames]);

  if (isDashboardLoading) {
    return (
      <Page showNav accountStatus={accountStatus}>
        <div className="flex justify-center items-center flex-1 pt-20">
          <AiOutlineSync className="animate-spin text-3xl text-[#233855]" />
        </div>
      </Page>
    );
  }

  return (
    <Page showNav accountStatus={accountStatus}>
      <div className="bg-white lg:bg-transparent flex-1 w-full">
      <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
      <div className="w-full lg:px-10 pt-[3vh] pb-28 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200">

          {/* NAGŁÓWEK */}
          <div className="mb-5 text-center">
            <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">
              Trening Dowolny
            </h1>
          </div>

          {/* JAK TO DZIAŁA */}
          <div className="w-full mb-5 px-1">
            <button
              type="button"
              onClick={() => setIsBenefitsOpen((v) => !v)}
              className="flex items-center justify-between w-full mb-2 group"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">Jak to działa?</span>
              <FiChevronDown className={`text-gray-300 group-hover:text-gray-500 transition-all duration-200 ${isBenefitsOpen ? 'rotate-180' : ''}`} size={14} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isBenefitsOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
              <TrainingHowItWorks />
            </div>
          </div>

          {/* WYSZUKIWARKA */}
          <button
            type="button"
            onClick={handleSearchTrigger}
            disabled={isSearchLoading}
            className="w-full flex items-center gap-3 bg-white border border-gray-300 hover:border-[#233855] hover:ring-2 hover:ring-[#233855]/10 rounded-xl px-4 py-3 mb-5 shadow-sm transition-all group disabled:opacity-70"
          >
            {isSearchLoading ? (
              <AiOutlineSync className="w-4 h-4 animate-spin text-[#233855]" />
            ) : (
              <FaSearch className="w-4 h-4 text-gray-400 group-hover:text-[#233855] transition-colors" />
            )}
            <span className="text-sm text-gray-500 group-hover:text-[#233855] transition-colors">
              {isSearchLoading ? "Pobieranie bazy zadań..." : "Znajdź zadanie maturalne, temat..."}
            </span>
          </button>

          {/* WYBRANE TEMATY + START (STICKY) */}
          {basket.length > 0 && (
            <div className="sticky top-0 z-20 bg-white -mx-4 px-4 lg:mx-0 lg:px-0 pt-3 pb-4 mb-3 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                  Wybrane tematy
                </span>
                <button
                  type="button"
                  onClick={() => setBasket([])}
                  className="text-xs font-bold text-[#8A0000] hover:underline uppercase"
                >
                  Wyczyść
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-5">
                {basket.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 bg-[#233855]/8 text-[#233855] text-xs font-medium px-2.5 py-1 rounded-full border border-[#233855]/15"
                  >
                    {subfieldNameMap[id] || "Temat"}
                    <button
                      type="button"
                      onClick={() => handleToggleBasket(id)}
                      className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-[#233855]/20 transition-colors ml-0.5"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={handleStartBasket}
                className="w-full flex justify-center items-center gap-2 rounded-xl bg-[#233855] px-6 py-4 text-base font-apotek tracking-widest text-slate-100 hover:bg-[#2a4263] active:scale-[0.98] transition-all duration-150 shadow-[0_4px_14px_rgba(35,56,85,0.25)]"
              >
                ROZWIĄZUJ · {basket.length} {basket.length === 1 ? "temat" : basket.length < 5 ? "tematy" : "tematów"}
              </button>
            </div>
          )}

          {/* GRUPY DZIAŁÓW — 1 kolumna */}
          {Object.entries(groupedFields).map(([groupName, groupFields]) => (
            <div key={groupName}>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 pt-4 pb-1 px-1">
                {groupName}
              </p>
              {groupFields.map((field) => (
                <FieldRow
                  key={field.field_id}
                  field={field}
                  onOpen={() => handleOpenSheet(field.field_id)}
                />
              ))}
            </div>
          ))}

      </div>
      </div>
      </div>


      {/* BOTTOM SHEET DZIAŁU */}
      <FieldSheet
        isOpen={!!selectedFieldId && !!selectedField}
        field={selectedField}
        subfields={selectedFieldId ? (subfieldsMap[selectedFieldId] || []) : []}
        isLoading={loadingFieldId === selectedFieldId}
        basket={basket}
        onToggleBasket={handleToggleBasket}
        onDirectStart={handleDirectStart}
        onClose={() => setSelectedFieldId(null)}
        onStartBasket={handleStartBasket}
      />

      {/* MODALS */}
      {isSearchModalOpen && (
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          data={searchData}
          currentSelection={basket}
          onSelect={handleSearchResultSelect}
        />
      )}

      {isPaywallPopupOpen && (
        <PaywallPopup
          isOpen={isPaywallPopupOpen}
          onClose={() => setPaywallPopupOpen(false)}
          onSubscribe={() => { setPaywallPopupOpen(false); navigate("/upgrade"); }}
          onClaimGracePeriod={async () => {
            try {
              const { data } = await api.post("activate-grace-period/");
              if (data.status === "success") {
                setPaywallPopupOpen(false);
                setAccountStatus((prev) => ({ ...prev, has_access: true, can_activate_grace_period: false }));
              }
            } catch (err) {
              console.error(err);
            }
          }}
          userData={popupUserData}
          accountStatus={accountStatus}
        />
      )}

      <SessionSetupPopup
        isOpen={isSessionLoading}
        isDataReady={isSessionDataReady}
        onComplete={handleSessionComplete}
        messages={sessionMessages}
      />
    </Page>
  );
};

export default Training;
